import { useEffect, useRef, useState } from 'react';
import { COLORS, OPTIONS, drawAvatar, randomLook, type Look } from '../game/avatar';

// The fish builder: students design the character fish they explore the ocean
// as. A live, animated preview; one row of choices per part; colour swatches.

type Props = {
  initial: Look;
  /** First time: there's no Cancel, a fish is needed to play. */
  required?: boolean;
  onSave: (look: Look) => Promise<void>;
  onCancel?: () => void;
};

const ROWS: { key: keyof typeof OPTIONS; label: string }[] = [
  { key: 'body', label: 'Body' }, { key: 'tail', label: 'Tail' }, { key: 'fin', label: 'Top fin' },
  { key: 'eyes', label: 'Eyes' }, { key: 'mouth', label: 'Mouth' }, { key: 'pattern', label: 'Pattern' }, { key: 'extra', label: 'Extra' },
];
const PAINTS: { key: 'bodyColor' | 'finColor' | 'patternColor'; label: string }[] = [
  { key: 'bodyColor', label: 'Body colour' }, { key: 'finColor', label: 'Fin colour' }, { key: 'patternColor', label: 'Pattern colour' },
];

export default function FishBuilder({ initial, required, onSave, onCancel }: Props) {
  const [look, setLook] = useState<Look>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lookRef = useRef(look);
  lookRef.current = look;

  // The preview swims in place.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const g = c.getContext('2d')!;
    let raf = 0;
    const draw = (now: number) => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = c.getBoundingClientRect();
      if (c.width !== Math.round(r.width * dpr)) { c.width = Math.round(r.width * dpr); c.height = Math.round(r.height * dpr); }
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.clearRect(0, 0, c.width, c.height);
      const t = now / 1000;
      drawAvatar(g, lookRef.current, { x: c.width / 2, y: c.height / 2 + Math.sin(t * 1.4) * 6 * dpr, size: Math.min(c.width, c.height * 1.6) * 0.62, effort: 0.35, t });
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  const set = <K extends keyof Look>(k: K, v: Look[K]) => setLook(l => ({ ...l, [k]: v }));

  const save = async () => {
    setBusy(true); setError(null);
    try { await onSave(look); } catch (e) { setError(e instanceof Error ? e.message : 'Could not save your fish.'); setBusy(false); }
  };

  return (
    <div className="builder-modal" role="dialog" aria-modal="true" aria-label="Design your fish">
      <div className="fb">
        <div className="fb-head">
          <h2>{required ? 'Design your fish' : 'Change your fish'}</h2>
          <p className="small muted">This is you in the class ocean. Your classmates see it at your spot.</p>
        </div>
        <div className="fb-body">
          <div className="fb-preview">
            <canvas ref={canvasRef} aria-hidden />
            <button type="button" className="small" onClick={() => setLook(randomLook())}>🎲 Surprise me</button>
          </div>
          <div className="fb-options">
            {ROWS.map(({ key, label }) => (
              <div key={key} className="fb-row">
                <span className="fb-label">{label}</span>
                <div className="fb-choices" role="radiogroup" aria-label={label}>
                  {OPTIONS[key].map(o => (
                    <button key={o.value} type="button" role="radio" aria-checked={look[key] === o.value}
                      className={look[key] === o.value ? 'on' : ''} onClick={() => set(key, o.value as Look[typeof key])}>{o.label}</button>
                  ))}
                </div>
              </div>
            ))}
            {PAINTS.map(({ key, label }) => (
              <div key={key} className="fb-row">
                <span className="fb-label">{label}</span>
                <div className="fb-swatches" role="radiogroup" aria-label={label}>
                  {COLORS.map(c => (
                    <button key={c} type="button" role="radio" aria-checked={look[key] === c} aria-label={c}
                      className={look[key] === c ? 'on' : ''} style={{ background: c }} onClick={() => set(key, c)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        {error && <div className="notice bad small">{error}</div>}
        <div className="fb-foot">
          {!required && onCancel && <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>}
          <button type="button" className="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : required ? 'Start exploring 🌊' : 'Save my fish'}</button>
        </div>
      </div>
    </div>
  );
}
